import {Form, Input, Button, Card, Typography} from 'antd';
import {useTranslation} from "react-i18next";
import {useEffect, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import ApiService from "../core/service/ApiService";
import PatientLocalStorage from "./PatientLocalStorage";
import Flex from "../core/components/Flex";
import BirthDateInput from "./BirthDateInput";
import { Branding } from "../core/service/BrandingService";
import "../app/auth/login.css";

export default function PatientLogin()
{
    const logoBackground = Branding.logoBackground('loginLogo');
    const {t} = useTranslation();
    const formRef = useRef();
    const history = useHistory();
    const [error, setError] = useState('')
    const [consentConfig, setConsentConfig] = useState({})
    const [authParams, setAuthParams] = useState({
        enabled: false,
        key: null,
        timeout: 0,
        sms_auth: false,
        mail_auth: false,
    });

    // Aydınlatma metni linkini (pasif bilgi) göstermek için config bir kez çekilir.
    useEffect(() => {
        ApiService.NotificationSettings.GetConsentConfig().then(res => {
            if (res.success) setConsentConfig(res.data || {});
        });
    }, []);

    const sendCode = async (type) => {
        setError('')

        const res = await ApiService.Auth.SendCode({
            code_key: authParams.key,
            code_type: type,
            grant_type: 'patient'
        })

        if(res.success) {
            setAuthParams({
                ...authParams,
                enabled: true,
                sent: true,                 // kod alanı sayaçtan BAĞIMSIZ görünür (sunucudaki kod ömrü 3 dk)
                key: res.data?.access_token,
                timeout: 60,                // yalnız "tekrar gönder" bekleme süresi
                sms_auth: authParams.sms_auth,
                mail_auth: authParams.mail_auth,
            })
            startTimer()
        }
    }

    const startTimer = () => {
        const interval = setInterval(() => {
            setAuthParams((prev) => {
                const nextTimeout = prev.timeout-1
                if(nextTimeout === 0) {
                    clearInterval(interval)
                }

                return {
                    ...prev,
                    timeout: prev.timeout-1
                }
            })
        }, 1000)
    }

    // Oturumu açar: token'ı saklar, hasta bilgisini çeker ve ana ekrana geçer.
    const completeLogin = async (token) => {
        PatientLocalStorage.saveToken(token)
        const res = await ApiService.PatientApp.GetPatient()
        if(res.success) {
            PatientLocalStorage.saveInformation(res.data)
        }
        redirectToHome()
    }

    const signIn = async (form) => {
        setError('')

        if(!authParams.enabled) {
            // Doğum tarihi 3 kutudan ({ day, month, year }) geliyor; baştaki sıfırlar parseInt ile atılıyor.
            const birth = form.birth || {};
            const passwordFromBirth = `${birth.year}-${parseInt(birth.month)}-${parseInt(birth.day)}`;

            const response = await ApiService.Auth.Login({
                username: form.username,
                password: passwordFromBirth,
                grant_type: 'patient',
            })
            if(response.success) {
                // Dönen access_token'ın ne olduğunu SUNUCU söyler (mfa_required): bilet mi, oturum mu.
                // Bunu "sms_auth || mail_auth" ile tahmin etmek eski bir hataydı — iki kanal da kapalı
                // dönen bir bilet oturum token'ı sanılıp kaydediliyor, ardından her istek 401 alıyordu.
                if(response.data.mfa_required) {
                    setAuthParams({
                        enabled: true,
                        sms_auth: response.data.sms_auth,
                        mail_auth: response.data.mail_auth,
                        timeout: 0,
                        key: response.data?.access_token,
                        sms_text: response.data.sms_text,
                        mail_text: response.data.mail_text,
                    })
                }
                else {
                    // Doğrulama kodu kapalı (App.PatientMfaEnabled=false): kimlik no + doğum tarihi
                    // doğrulandı, gelen token doğrudan oturum token'ıdır.
                    await completeLogin(response.data?.access_token)
                }
            }
            else {
                setError(t('Please check your information!'))
            }
        }
        else {
            const verifyResponse = await ApiService.Auth.SmsVerify({
                password: form.code,
                code_key: authParams.key,
                grant_type: 'patient'
            })

            if(verifyResponse.success) {
                await completeLogin(verifyResponse.data?.access_token)
            }
            else {
                setError(t('Verification code is wrong!'))
            }
        }
    }

    const redirectToHome = () => {
        if(PatientLocalStorage.isAuth())
        {
            history.push(`/`)
        }
    }

    useEffect(() => {
        redirectToHome()
    }, [])

    return (
        <>
            <Flex
                flexDirection={'column'}
                alignItems={'center'}
                style={{
                    height: '100%',
                    backgroundColor: 'var(--bg)',
                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${Branding.asset('loginBackground')})`,
                }}
            >
                <Card style={{marginTop: 48, marginBottom: 32}}>
                    {/* kiracının logosu kartın içinde; şeffaf değilse marka ayarındaki renk kutusunda */}
                    <div className="login-logo">
                        <span
                            className={`login-logo-box ${logoBackground ? 'has-bg' : ''}`}
                            style={logoBackground ? { background: logoBackground } : undefined}
                        >
                            <img src={Branding.asset('loginLogo')} alt={Branding.displayName() || 'Logo'} />
                        </span>
                    </div>
                    <Form
                        style={{width: 320}}
                        ref={formRef}
                        name="basic"
                        layout={'vertical'}
                        initialValues={{}}
                        autoComplete="off"
                        onFinish={signIn}
                    >
                        <Form.Item
                            label={t('Identity Number')}
                            name="username"
                            rules={[{required: true, message: t('Please input your identity number!')}]}
                        >
                            <Input
                                size={'large'}
                                disabled={authParams.enabled}/>
                        </Form.Item>

                        {
                            !authParams.enabled && (
                                <Form.Item
                                    label={<span>{t('Birth Date')} <span style={{color: '#808080'}}>({t('DD/MM/YYYY')})</span></span>}
                                    name={'birth'}
                                    rules={[{
                                        validator: (_, v) => (v && v.day && v.month && v.year)
                                            ? Promise.resolve()
                                            : Promise.reject(new Error(t('Please input your birth date!')))
                                    }]}
                                >
                                    <BirthDateInput />
                                </Form.Item>
                            )
                        }

                        {
                            authParams.enabled && (
                                <>
                                    <Form.Item
                                        label={' '}
                                        colon={false}
                                    >
                                        <div style={{width: '100%', wordWrap: 'break-word', wordBreak: 'break-word'}}>
                                            {t('You need to verify the security code to log in to your account.')}
                                        </div>
                                        <div style={{marginTop: 8}}>
                                            {
                                                authParams.sms_auth && (
                                                    <Button
                                                        size={'large'}
                                                        style={{width: '100%', height: 45}}
                                                        disabled={authParams.timeout > 0}
                                                        onClick={() => {
                                                            sendCode('sms')
                                                        }}
                                                    >
                                                        {t('SMS') + ` (*** *** **${authParams.sms_text})`}
                                                    </Button>
                                                )
                                            }
                                            {
                                                authParams.mail_auth && (
                                                    <Button
                                                        size={'large'}
                                                        style={{width: '100%', height: 45}}
                                                        disabled={authParams.timeout > 0}
                                                        onClick={() => {
                                                            sendCode('mail')
                                                        }}
                                                    >
                                                        {t('Mail') + ` (${authParams.mail_text})`}
                                                    </Button>
                                                )
                                            }
                                        </div>
                                    </Form.Item>
                                    {
                                        authParams.sent && (
                                            <Form.Item
                                                label={(
                                                    <div style={{display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center'}}>
                                                        <span>{t('Code')}</span>
                                                        {/* Mobilde addon yerine label hizasında timer; daha kompakt */}
                                                        <span style={{color: '#8c8c8c', fontSize: 12}}>
                                                            ({authParams.timeout})
                                                        </span>
                                                    </div>
                                                )}
                                                name="code"
                                                rules={[{required: true, message: t('Please input your verification code!') }]}
                                            >
                                                <Input
                                                    size={'large'}
                                                    inputMode="numeric"
                                                    autoComplete="one-time-code"
                                                />
                                            </Form.Item>
                                        )
                                    }
                                </>
                            )
                        }

                        <Typography.Text style={{color: '#ff0000', fontWeight: 'bold', marginBottom: 16, fontSize: '11pt'}}>
                            {t(error)}
                        </Typography.Text>

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                style={{width: '100%', height: 48, fontSize: '16pt'}}
                            >
                                {
                                    !authParams.enabled ? t('Login') : t('Send')
                                }
                            </Button>
                        </Form.Item>

                        {
                            // Aydınlatma metni: onay değil, yalnızca bilgilendirme. Login altında anlamlı bir cümleyle
                            // metne erişim linki verilir (checkbox/"okudum onayladım" yok).
                            !authParams.enabled && consentConfig?.aydinlatmaUrl && (
                                <div style={{textAlign: 'center', marginTop: 4, fontSize: '11pt', color: '#595959'}}>
                                    <a href={consentConfig.aydinlatmaUrl} target="_blank" rel="noreferrer">
                                        {t('You can access the clarification text here.')}
                                    </a>
                                </div>
                            )
                        }
                    </Form>
                    {/* Üretici imzası — beyaz etiket ayarıyla KALDIRILAMAZ (kodda koşulsuz). */}
                    <div className="login-company">
                        <a
                            className="login-company-row"
                            href="https://kuantumbulut.com"
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            <img src="/logo-his-light.svg" alt="HIS · eCloudPACS" className="login-company-product" />
                            <span className="login-company-site">kuantumbulut.com</span>
                        </a>
                        <div className="login-company-copy">
                            © {new Date().getFullYear()} Kuantum Bulut Bilişim ve Yazılım A.Ş.
                        </div>
                    </div>
                </Card>
            </Flex>
        </>
    )
}
